'use client';

import React from 'react';
import {
  LayoutDashboard,
  FileText,
  TrendingUp,
  DollarSign,
  Users,
  Target,
  BarChart3,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SidebarProps {
  activeModule: string;
  setActiveModule: (module: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ activeModule, setActiveModule, isOpen, onClose }: SidebarProps) {
  const menuItems = [
    {
      group: "메인",
      items: [
        { id: 'dashboard', label: '대시보드', icon: LayoutDashboard }
      ]
    },
    {
      group: "직무 관리",
      items: [
        { id: 'job-analysis', label: '직무분석', icon: FileText },
        { id: 'job-evaluation', label: '직무평가', icon: TrendingUp }
      ]
    },
    {
      group: "보상 설계",
      items: [
        { id: 'salary-design', label: '직무급 설계', icon: DollarSign },
        { id: 'performance', label: '성과·보상', icon: Target }
      ]
    },
    {
      group: "연계 관리",
      items: [
        { id: 'hr-integration', label: '채용/승진/교육', icon: Users }
      ]
    },
    {
      group: "분석 & 설정",
      items: [
        { id: 'reports', label: '리포트 & 설정', icon: BarChart3 }
      ]
    }
  ];

  return (
    <aside className={`
      fixed lg:static inset-y-0 left-0 z-50
      w-56 min-h-full bg-white border-r flex-shrink-0
      transform transition-transform duration-300 ease-in-out
      ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    `}>
      {/* Header */}
      <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <LayoutDashboard className="h-4 w-4 text-white" />
            </div>
            <h2 className="text-base font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              HR 프로세스
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-8 w-8"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Menu */}
      <nav className="p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
        {menuItems.map((group, idx) => (
          <div key={group.group} className={idx > 0 ? "mt-4 pt-3 border-t" : ""}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 px-2 py-1 mb-2 bg-gray-50 rounded relative pl-3">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-gradient-to-b from-blue-500 to-purple-600 rounded-r-full" />
              {group.group}
            </div>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveModule(item.id)}
                  className={`
                    w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-200 text-left relative
                    ${activeModule === item.id
                      ? 'bg-gradient-to-r from-blue-50 to-purple-50 shadow-sm'
                      : 'hover:bg-gray-50'
                    }
                  `}
                >
                  {activeModule === item.id && (
                    <div className="absolute left-0 w-0.5 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-r-full" />
                  )}
                  <item.icon className={`
                    h-4 w-4 flex-shrink-0 transition-colors duration-200
                    ${activeModule === item.id
                      ? 'text-blue-600'
                      : 'text-gray-500'
                    }
                  `} />
                  <span className={`
                    text-xs transition-colors duration-200 whitespace-nowrap
                    ${activeModule === item.id
                      ? 'font-semibold text-gray-900'
                      : 'text-gray-600'
                    }
                  `}>
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
